import React, { useMemo } from "react";
import { StickyConfigurator } from "tsi-common-react/src/apps/configurator/StickyConfigurator/index";
import {
    UpsellModal,
    extractUpsellBlock,
} from "tsi-common-react/src/apps/configurator/UpsellModal";
import {
    IUpsellModalComponentFn,
    ModularConfiguratorOptionSet,
} from "tsi-common-react/src/apps/configurator/models";
import { prefillSSRStoreData } from "tsi-common-react/src/apps/configurator/ssr";
import { isoWebPageURL } from "tsi-common-react/src/models/nominals";
import { check } from "tsi-common-react/src/models/utils";
import { useSSRStorePrefill } from "tsi-common-react/src/store/reducers";
import { usePageURL } from "tsi-common-react/src/utils/urls";

import { templates } from "@reactivated";

export const Template = (
    props: templates.StickyConfiguratorTemplate,
): React.JSX.Element => {
    const financeLink = usePageURL("finance_link");
    const home = usePageURL("home");

    // Parse configurator option set for initial_variant_id and product_ids
    const optionSet = useMemo(
        () =>
            check(
                ModularConfiguratorOptionSet.decode(
                    JSON.parse(props.configurator_init_data),
                ),
            ),
        [props.configurator_init_data],
    );

    // Hydrate Redux store with SSR data before render
    useSSRStorePrefill(
        {
            products_data: props.products_data,
            product_class_data: props.product_class_data,
            financing_plans_data: props.financing_plans_data,
            concrete_bundles_data: props.concrete_bundles_data,
            upsell_info_modals_data: props.upsell_info_modals_data,
            promo_cards_data: props.promo_cards_data,
            reviews_products_data: props.reviews_products_data,
            initial_variant_id: optionSet.initial_variant_id,
            product_ids: optionSet.product_ids,
        },
        prefillSSRStoreData,
    );

    const blockData = useMemo(
        () => extractUpsellBlock(props.bundle_modal_page),
        [props.bundle_modal_page],
    );

    // Stable component function — must not change identity between renders
    // or React will unmount/remount the modal and lose its state.
    const upsellModalComponentFn = useMemo<IUpsellModalComponentFn | null>(
        () =>
            blockData
                ? (modalProps) => (
                      <UpsellModal {...modalProps} blockData={blockData} />
                  )
                : null,
        [blockData],
    );

    const showUpsellModal = useMemo(
        () => () => blockData !== null,
        [blockData],
    );

    const getUpsellModalComponentFn = useMemo(
        () => () => upsellModalComponentFn,
        [upsellModalComponentFn],
    );

    return (
        <StickyConfigurator
            basketLink={isoWebPageURL.wrap(props.basket_link)}
            configureGiftsLink={isoWebPageURL.wrap(props.configure_gifts_link)}
            financingLink={financeLink || home}
            isPLCVersion={props.is_plc_version}
            categorySelectors={props.category_selectors_data}
            stickyTriggerSelector={props.sticky_trigger_selector}
            editScrollTargetSelector={props.edit_scroll_target_selector}
            showUpsellModal={showUpsellModal}
            getUpsellModalComponentFn={getUpsellModalComponentFn}
            upsellModal={upsellModalComponentFn ?? undefined}
            strikeThroughMSRP={false}
            actualPriceStyle={""}
            overrideFinancingCopy={""}
        />
    );
};
